"use client";

import Image from "next/image";
import { useState } from "react";

const BRIDGE_FEATURES = [
  {
    title: "Online Training Academy",
    body: "Access training in addiction recovery, small group facilitation, and specialised programme delivery, anytime, from anywhere.",
    img: "/images/partner/getting-started/GettingStarted_LowerImageRight_01.webp",
    imgAlt: "Bridge platform: user profile and Academy training interface.",
  },
  {
    title: "Detailed Facilitator Guides",
    body: "Reference step-by-step manuals containing fail-proof guidelines for delivering dynamic groups and programmes.",
    img: "/images/partner/getting-started/GettingStarted_LowerImageRight_02.webp",
    imgAlt: "Bridge platform: facilitator guide document view.",
  },
  {
    title: "A Storehouse of Resources",
    body: "Rely on our extensive library of recovery teachings, topics, presentations, documents, and tools, to bring depth and impact to your recovery services.",
    img: "/images/partner/getting-started/GettingStarted_LowerImageRight_03.webp",
    imgAlt: "Bridge platform: resources library view.",
  },
] as const;

export function WhatIsBridge() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Centred intro — matches live site text-align-center layout */}
        <div className="mx-auto mb-14 max-w-2xl space-y-4 text-center">
          <h2 className="heading-2 font-bold">What is Bridge?</h2>
          <p className="body-lg text-muted-foreground">
            We believe that{" "}
            <strong className="text-foreground">
              empowered facilitators create powerful recovery spaces.
            </strong>{" "}
            Bridge is your recovery services platform. Every subscription
            includes online training, recovery resources and management tools.
            Everything you need to launch and run successful recovery groups and
            programmes in your community.
          </p>
        </div>

        {/* 2-col: clickable feature list + synchronized image */}
        <div className="grid gap-12 md:grid-cols-2 md:gap-16">
          {/* Feature list — on mobile each item embeds its own image */}
          <div className="flex flex-col">
            {BRIDGE_FEATURES.map((feature, i) => {
              const isActive = activeIndex === i;
              return (
                <button
                  className={`flex flex-1 cursor-pointer flex-col justify-center border-l px-6 py-6 text-left transition-colors duration-200 md:px-8 ${
                    isActive
                      ? "border-[var(--brand-orange)] bg-muted shadow-sm"
                      : "border-transparent"
                  }`}
                  key={feature.title}
                  onClick={() => setActiveIndex(i)}
                  type="button"
                >
                  <h3
                    className={`heading-6 mb-2 transition-colors duration-200 ${
                      isActive
                        ? "font-bold text-foreground"
                        : "font-semibold text-muted-foreground"
                    }`}
                  >
                    {feature.title}
                  </h3>
                  <p className="body-base text-muted-foreground">
                    {feature.body}
                  </p>
                  {/* Inline image — mobile only */}
                  {isActive && (
                    <div className="relative mt-4 aspect-[617/641] overflow-hidden rounded-lg md:hidden">
                      <Image
                        alt={feature.imgAlt}
                        className="object-cover"
                        fill
                        sizes="100vw"
                        src={feature.img}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right — synced image, desktop only */}
          <div className="relative hidden aspect-[617/641] overflow-hidden md:block">
            {BRIDGE_FEATURES.map((feature, i) => (
              <div
                className="absolute inset-0 transition-opacity duration-500 ease-in-out"
                key={feature.img}
                style={{ opacity: activeIndex === i ? 1 : 0 }}
              >
                <Image
                  alt={feature.imgAlt}
                  className="object-cover"
                  fill
                  sizes="50vw"
                  src={feature.img}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
